import {useCommonItems} from "../../../../contexts/CommonItensProvider";
import {useAppContext} from "../../../../contexts/AppContext";
import {useEffect, useMemo, useState} from "react";
import dayjs from "dayjs";
import {Controller, useForm} from "react-hook-form";
import {formatDateTime, parseDateTime} from "../../../../utils/format-date";
import {carregaLockers} from "../equipments/configs/functions";
import {getAbastecimentoData} from "./configs/functions";
import {validateRequiredFields} from "../../../../utils/validate-required-fields";
import {checkPermissionsAndRedirect, getFormErrorMessage, verifyFieldsAreFilled} from "../../../../configs/functions";
import {listaEmpresas} from "../../management/companies/configs/functions";
import {useQuery} from "@tanstack/react-query";
import {exportSupplyLockerToPDF} from "./configs/utils";
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Grid,
    LinearProgress,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";
import LinkNavegacao from "../../../common/Link";
import HomeIcon from "@mui/icons-material/Home";
import PageTitle from "../../../common/PageTitle";
import {LockerIcon} from "../../../common/Icons";
import {SelectEmpresaFilter} from "../../../common/SelectEmpresaFilter";
import {TooltipSelectDisabled} from "../../../common/TooltipSelectDisabled";
import DateRangePicker from "../../../common/DateRangePicker";
import {FilterAlt} from "@mui/icons-material";
import {DataGrid, ptBR} from "@mui/x-data-grid";
import {CustomToolbarReports} from "../../../common/CustomToolbarReports";

export function AbastecimentoArmario() {
    const { exibirAlerta, exibirDialog, handleCloseDialog } = useCommonItems();
    const { dadosUsuario, getEmpresaIdSession, setEmpresaIdSession } = useAppContext();

    const [numTotalItems, setNumTotalItems] = useState(0);

    const [supplyLocker, setDataSupplyLocker] = useState([]);
    const [armarios, setArmarios] = useState([]);

    const initialDataInicio = dayjs().subtract(1, 'month')
    const initialDataFim = dayjs()

    const [dateRange, setDateRange] = useState([initialDataInicio, initialDataFim]);

    const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSearched, setIsSearched] = useState(false);

    const {
        handleSubmit,
        setValue,
        getValues,
        clearErrors,
        reset,
        control,
        setError,
        formState: { errors },
    } = useForm();

    const columns = useMemo(() => [
        { field: 'armario', headerName: 'Armário', minWidth: 200, flex: 1 },
        { field: 'porta', headerName: 'Porta', minWidth: 80, flex: 0.5 },
        { field: 'produtoCa', headerName: 'CA', minWidth: 120, flex: 0.5 },
        { field: 'produtoNome', headerName: 'Produto', minWidth: 280, flex: 0.5 },
        { field: 'produtoCodExterno', headerName: 'Cód. Produto', minWidth: 120, flex: 0.5 },
        { field: 'reposição', headerName: 'Quantidade', minWidth: 90, flex: 0.5 },
        { field: 'classificacaoMovimentacao', headerName: 'Classificação', minWidth: 120, flex: 0.5 },
        { field: 'abastecedor', headerName: 'Abastecedor', minWidth: 140, flex: 0.8 },
        { field: 'area', headerName: 'Área', minWidth: 200, flex: 1 },
        { field: 'setor', headerName: 'Setor', minWidth: 200, flex: 1 },
        { field: 'endereco', headerName: 'Endereço', minWidth: 200, flex: 1 },
        { field: 'str', headerName: 'STR', minWidth: 90, flex: 0.5 },
        {
            field: 'data',
            headerName: 'Data',
            minWidth: 200,
            flex: 1,
            type: 'dateTime',
            valueGetter: (params) => parseDateTime(params.row?.data),
            renderCell: (params) => formatDateTime(params.row?.data, "DD/MM/YYYY HH:mm:ss"),
        }
    ], []);

    const { data: empresasData, error: listaEmpresasError, isLoading: isLoadingEmpresasData, isPending } = useQuery({
        queryKey: ['listaEmpresas'],
        queryFn: async () => {
            const response = await listaEmpresas(null, null, null, true);
            const empresasData = response?.data.data;

            let defaultEmpresaId = null;
            const selectedEmpresaIdSession = getEmpresaIdSession();
            if (selectedEmpresaIdSession) {
                defaultEmpresaId = selectedEmpresaIdSession;
            } else {
                defaultEmpresaId = empresasData[0].id;
                setEmpresaIdSession(empresasData[0].id);
            }

            setSelectedEmpresaId(defaultEmpresaId);

            const defaultValues = {
                id_empresas: defaultEmpresaId,
                id_armario: '',
                data_inicio: initialDataInicio,
                data_fim: initialDataFim,
            };
            reset(defaultValues);

            loadReportData(defaultEmpresaId, '', initialDataInicio, initialDataFim);

            return response;
        },
        refetchOnWindowFocus: false,
    });

    const empresas = empresasData?.data.data || [];
    const isLoadingEmpresas = isLoadingEmpresasData || isPending;

    const listaArmarios = async (empresaId) => {
        try {
            exibirDialog('Carregando Armário...');

            const filterAll = { id_empresas: Array.isArray(empresaId) ? empresaId : [empresaId], isFilter: true };
            const response = await carregaLockers(filterAll);

            setArmarios(response.data.data);
        } catch (error) {
            exibirAlerta('Erro ao carregar os Armários', '', 'error');
        } finally {
            handleCloseDialog();
        }
    }

    async function loadReportData(id_empresas, id_armario, data_inicio, data_fim) {
        try {
            setIsLoading(true);

            data_inicio = dayjs(data_inicio).format('YYYY-MM-DD');
            data_fim = dayjs(data_fim).format('YYYY-MM-DD');

            const filter = {
                id_empresas: [id_empresas],
                data_inicio,
                data_fim,
            };

            if (id_armario) filter.id_armario = id_armario;

            const res = await getAbastecimentoData(filter);

            setDataSupplyLocker(res.data.movimentacoes);
            setNumTotalItems(res.data.numero_total);
        } catch (error) {
            console.log(error);
            exibirAlerta('Ops', 'Ocorreu um erro ao carregar dados do relatório.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async (data) => {
        try {
            const isFilled = await validateRequiredFields(getValues, setError, clearErrors, ['id_empresas', 'data_inicio', 'data_fim']);

            if (!isFilled) throw new Error('Preencha os campos obrigatórios');

            setIsSearched(true);
            loadReportData(data.id_empresas, data.id_armario, data.data_inicio, data.data_fim);
        } catch (error) {
            exibirAlerta('Erro', 'Erro ao carregar dados do relatório', 'error');
            setIsSearched(false);
        }
    };

    function getDataEmpresa(empresaId) {
        listaArmarios(empresaId);
    }

    useEffect(() => {
        const requiredPermissionsView = ["admin", "admin_relatorio"];
        checkPermissionsAndRedirect(requiredPermissionsView);

        return () => {
            reset();
        }
    }, [reset]);

    useEffect(() => {
        if (selectedEmpresaId) {
            getDataEmpresa(selectedEmpresaId);
        }
    }, [selectedEmpresaId]);

    useEffect(() => {
        const id_empresas = selectedEmpresaId;
        const id_armario = getValues('id_armario');
        const data_inicio = getValues('data_inicio')
        const data_fim = getValues('data_fim')

        if (!id_empresas || !data_inicio || !data_fim) {
            return;
        }

        loadReportData(id_empresas, id_armario, data_inicio, data_fim);
    }, []);

    useEffect(() => {
        if (listaEmpresasError) {
            exibirAlerta('Erro', 'Erro ao carregar empresas', 'error');
        }
    }, [exibirAlerta, listaEmpresasError]);

    function exportDataToPDF(visibleColumns, reportName) {
        try {
            if (!getValues('id_empresas')) {
                return exibirAlerta('Ops', 'Selecione ao menos empresa', 'warning');
            }

            if (!getValues('data_inicio') || !getValues('data_fim')) {
                return exibirAlerta('Ops', 'Selecione um período para exportar', 'warning');
            }

            exportSupplyLockerToPDF({
                tableData: supplyLocker,
                visibleColumns,
                reportName,
                data_inicio: getValues('data_inicio'),
                data_fim: getValues('data_fim'),
                selectEmpresa: empresas.find(empresa => empresa.id === Number(selectedEmpresaId)),
                dadosUsuario
            })
        } catch (error) {
            exibirAlerta('Ops', 'Erro ao exportar dados para PDF', 'error');
            console.log(error);
        }
    }

    return (
        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="stretch"
            spacing={3}
        >
            <Grid item xs={12}>
                <Breadcrumbs>
                    <LinkNavegacao to='/'><HomeIcon fontSize='small' /></LinkNavegacao>
                    <LinkNavegacao to='/epis'>Gestão de EPI's</LinkNavegacao>
                    <LinkNavegacao to='/epis/relatorios'>Relatórios</LinkNavegacao>
                    <Typography variant='span'>Abastecimento de Armários</Typography>
                </Breadcrumbs>
                <PageTitle icon={<LockerIcon fontSize='large' />} title="Abastecimento de Armários" />
            </Grid>

            <Grid container item xs={12} spacing={3} component="form" onSubmit={handleSubmit(onSubmit)}>
                <Grid container item xs={12} md={12} spacing={3}>
                    <Grid item xs={12} md={6}>
                        <SelectEmpresaFilter
                            empresas={empresas}
                            id_empresas={empresas}
                            defaultValue={selectedEmpresaId}
                            onChangeValue={(value) => {
                                setIsSearched(false);
                                setSelectedEmpresaId(value);
                                setEmpresaIdSession(value);
                                setValue('id_empresas', value, { shouldDirty: true });
                                setValue('id_armario', '', { shouldDirty: true });
                            }}
                            textHelper={false}
                            isLoading={isLoadingEmpresas}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TooltipSelectDisabled isDisabled={selectedEmpresaId}>
                            <Controller
                                name='id_armario'
                                control={control}
                                rules={{ required: false }}
                                defaultValue={''}
                                render={({ field: { ref, onChange, ...field } }) => (
                                    <TextField
                                        {...field}
                                        select
                                        fullWidth
                                        label="Armário"
                                        disabled={!selectedEmpresaId}
                                        onChange={(e) => {
                                            setIsSearched(false);
                                            setValue('id_armario', e.target.value, { shouldDirty: true })
                                            if (e.target.value) {
                                                clearErrors("id_armario");
                                            }
                                        }}
                                        error={!!errors.id_armario}
                                        helperText={getFormErrorMessage(errors, 'id_armario')}
                                        size='small'
                                        InputProps={{ style: { backgroundColor: '#fff' } }}
                                    >
                                        {armarios.length > 0 ? (
                                            [
                                                ...armarios.filter(option => option.status !== 0).map(option => (
                                                    <MenuItem key={option.id} value={option.id}>
                                                        {option.nome} - {option.tagIdentificacao} ({option.numeroPortas} portas)
                                                    </MenuItem>
                                                )),
                                                ...armarios.filter(option => option.status === 0).map(option => (
                                                    <MenuItem key={option.id} value={option.id} disabled>
                                                        {option.nome} - {option.tagIdentificacao} ({option.numeroPortas} portas) - (Inativo)
                                                    </MenuItem>
                                                ))
                                            ]
                                        ) : (
                                            <MenuItem disabled>Nenhum disponível</MenuItem>
                                        )}
                                    </TextField>
                                )}
                            />
                        </TooltipSelectDisabled>
                    </Grid>
                </Grid>

                <Grid container item xs={12} md={12} spacing={3}>
                    <Grid item xs={12} md={4}>
                        <DateRangePicker
                            value={dateRange}
                            bgColor='#fff'
                            onChange={(value) => {
                                setDateRange(value);
                                const [start, end] = value;
                                setValue('data_inicio', start ? dayjs(start).format('YYYY-MM-DD') : '');
                                setValue('data_fim', end ? dayjs(end).format('YYYY-MM-DD') : '');
                                setIsSearched(false);
                            }}
                            onReset={() => {
                                setDateRange([null, null]);
                                setValue('data_inicio', '');
                                setValue('data_fim', '');
                                setIsSearched(false);
                            }}
                            hasError={errors.data_inicio || errors.data_fim}
                        />
                    </Grid>

                    <Grid item xs={12} md={1}>
                        <Button
                            type='submit'
                            color='primary'
                            variant='contained'
                            disabled={
                                isLoading ||
                                Object.keys(errors).length > 0 ||
                                !verifyFieldsAreFilled([getValues('id_empresas')])
                            }
                            startIcon={isLoading ? <CircularProgress size={16} sx={{ color: "textSecondary" }} /> : <FilterAlt />}
                        >
                            Filtrar
                        </Button>
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <Box sx={{
                    height: supplyLocker.length > 0 ? "auto" : 400,
                    minHeight: 400,
                    width: '100%',
                    '& .MuiDataGrid-columnHeaders, .MuiDataGrid-toolbarContainer': {
                        backgroundColor: '#d8d8d8',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        borderRadius: 0
                    },
                }}>
                    <DataGrid
                        getRowId={(row) => row.idMovimentacao}
                        rows={supplyLocker}
                        columns={columns}
                        loading={isLoading}
                        pageSizeOptions={[10, 20, 50, 100]}
                        rowCount={numTotalItems}
                        paginationMode="client"
                        sx={{
                            backgroundColor: '#fff',
                            '.MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 'bold !important',
                                overflow: 'visible !important',
                            }
                        }}
                        disableRowSelectionOnClick
                        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
                        slots={{
                            toolbar: (props) =>
                                <CustomToolbarReports
                                    reportName="relatorio_abastecimento_armario"
                                    pdfExport={exportDataToPDF}
                                    props={props}
                                />
                            ,
                            loadingOverlay: LinearProgress,
                            noRowsOverlay: () => (
                                <Box style={{ display: "flex", width: '100%', textAlign: 'center', height: "100%", alignItems: 'center', justifyContent: 'center' }}>
                                    {isSearched ?
                                        <Typography variant='h6' color='textSecondary'>Nenhum registro encontrado para o filtro selecionado</Typography>
                                        :
                                        <Typography variant='h6' color='textSecondary'>Selecione uma empresa e o período que deseja filtrar</Typography>
                                    }
                                </Box>
                            )
                        }}
                    />
                </Box>
            </Grid>
        </Grid>
    );
}